/**
 * React Query hook for Batch Billing List with RDS-based pagination
 * Uses the batch-billing-list-rds API with filtering and sorting
 */

import { useQuery } from '@tanstack/react-query';
import { listBatchBilling } from 'services/billingServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Fetches batch billing data with RDS-based pagination
 * API Response format: { total_count, data: [], page_count, page, limit }
 *
 * @param {Object} params - Request parameters
 * @returns {Promise<Object>} - Response with data array and pagination metadata
 */
const fetchBatchBillingList = async ({
  limit = 30,
  page = 1,
  lead_attorney_list = [],
  lead_paralegal_list = [],
  practice_area_list = [],
  search_keyword = '',
  uninvoiced_billable_time = true,
  uninvoiced_nonbillable_time = true,
  uninvoiced_expenses = true,
  unpaid_invoice_balance = true,
  case_list = [],
} = {}) => {
  try {
    const response = await listBatchBilling({
      limit,
      page,
      lead_attorney_list,
      lead_paralegal_list,
      practice_area_list,
      search_keyword,
      uninvoiced_billable_time,
      uninvoiced_nonbillable_time,
      uninvoiced_expenses,
      unpaid_invoice_balance,
      case_list,
    });

    if (response?.data) {
      // When case_list is provided, API returns a summary object instead of array
      // Check if it's a summary object (has case_count) or regular list (has data array)
      if (case_list && case_list.length > 0 && response.data.case_count !== undefined) {
        // This is a summary response, return it as-is
        return response.data;
      }
      // Regular list response
      return response.data;
    }

    // Return empty structure if no data (matching API format)
    return {
      total_count: 0,
      data: [],
      page_count: 0,
      page: page,
      limit: limit,
    };
  } catch (error) {
    console.error('Error fetching batch billing list:', error);
    throw error;
  }
};

/**
 * Hook to fetch batch billing list with RDS-based pagination using React Query
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Items per page (default: 30)
 * @param {number} options.page - Page number (default: 1)
 * @param {Array} options.lead_attorney_list - Filter by lead attorney IDs
 * @param {Array} options.lead_paralegal_list - Filter by lead paralegal IDs
 * @param {Array} options.practice_area_list - Filter by practice area IDs
 * @param {string} options.search_keyword - Search keyword for filtering cases
 * @param {boolean} options.uninvoiced_billable_time - Include uninvoiced billable time (default: true)
 * @param {boolean} options.uninvoiced_nonbillable_time - Include uninvoiced non-billable time (default: false)
 * @param {boolean} options.uninvoiced_expenses - Include uninvoiced expenses (default: true)
 * @param {boolean} options.unpaid_invoice_balance - Include unpaid invoice balance (default: true)
 * @param {Array} options.case_list - Filter by specific case UUIDs (default: [])
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} Query result object
 * @returns {Array} returns.batchBillingList - Array of batch billing objects
 * @returns {number} returns.totalRecords - Total number of records
 * @returns {number} returns.currentPage - Current page number
 * @returns {number} returns.totalPages - Total number of pages
 * @returns {number} returns.pageSize - Items per page
 * @returns {boolean} returns.isLoading - Initial loading state
 * @returns {boolean} returns.isFetching - Fetching state (including background refetch)
 * @returns {boolean} returns.isError - Error state indicator
 * @returns {Object|null} returns.error - Error object if request failed
 * @returns {Function} returns.refetch - Function to manually refetch data
 *
 * @example
 * const {
 *   batchBillingList,
 *   totalRecords,
 *   totalPages,
 *   isLoading,
 *   refetch
 * } = useBatchBillingListQuery({
 *   limit: 30,
 *   page: 1,
 *   lead_attorney_list: ['attorney1'],
 *   practice_area_list: [1, 2],
 * });
 */
export const useBatchBillingListQuery = ({
  limit = 30,
  page = 1,
  lead_attorney_list = [],
  lead_paralegal_list = [],
  practice_area_list = [],
  search_keyword = '',
  uninvoiced_billable_time = true,
  uninvoiced_nonbillable_time = true,
  uninvoiced_expenses = true,
  unpaid_invoice_balance = true,
  case_list = [],
  enabled = true,
} = {}) => {
  // Create stable query key based on all filter parameters
  const queryKey = queryKeys.batchBilling.list(
    {
      lead_attorney_list,
      lead_paralegal_list,
      practice_area_list,
      search_keyword,
      uninvoiced_billable_time,
      uninvoiced_nonbillable_time,
      uninvoiced_expenses,
      unpaid_invoice_balance,
      case_list,
    },
    page,
    limit
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchBatchBillingList({
        limit,
        page,
        lead_attorney_list,
        lead_paralegal_list,
        practice_area_list,
        search_keyword,
        uninvoiced_billable_time,
        uninvoiced_nonbillable_time,
        uninvoiced_expenses,
        unpaid_invoice_balance,
        case_list,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (React Query v4 uses gcTime instead of cacheTime)
    keepPreviousData: true, // Keep previous data while fetching
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: enabled, // Only refetch on mount if data is stale (prevents duplicate calls)
    refetchOnReconnect: false, // Don't refetch on reconnect
    retry: (failureCount) => {
      // Retry twice for errors
      return failureCount < 2;
    },
  });

  // Check if response is a summary object (when case_list is provided) or regular list
  const isSummaryResponse = data?.case_count !== undefined;

  return {
    batchBillingList: isSummaryResponse ? data : data?.data || [], // Return summary object or array
    batchSummary: isSummaryResponse ? data : null, // Store summary separately if it's a summary response
    totalRecords: data?.total_count || data?.case_count || 0,
    currentPage: data?.page || page,
    totalPages: data?.page_count || 0,
    pageSize: data?.limit || limit,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export default useBatchBillingListQuery;
